import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { from, map, shareReplay, startWith } from 'rxjs';

import { AppMaterialModule } from '../../../app-material.module';
import {
  MemberModel,
  MemberService,
} from '../../../services/member/member.service';

@Component({
  selector: 'app-request-client-filter',
  templateUrl: './request-client-filter.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, AppMaterialModule],
  styleUrls: ['./request-client-filter.component.scss'],
})
export class RequestClientFilterComponent {
  public readonly ALL_CLIENTS_SELECTED: MemberModel = {
    memberId: -1,
    memberName: 'All Clients',
  };

  constructor(public memberService: MemberService) {}

  protected readonly allClients$ = from(
    this.memberService.getUserMembers(),
  ).pipe(
    map(allClients =>
      allClients.sort((a, b) => a.memberName.localeCompare(b.memberName)),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly selectedClientsControl = new FormControl<MemberModel[]>({
    value: [],
    disabled: false,
  });

  public readonly selectedClients = outputFromObservable<MemberModel[]>(
    this.selectedClientsControl.valueChanges.pipe(
      map(selectedClients => this.filterSelectedClients(selectedClients)),
      shareReplay({ bufferSize: 1, refCount: true }),
      startWith([]),
    ),
  );

  public toggleAllClients(
    control: FormControl<MemberModel[]>,
    options: MemberModel[],
  ) {
    if (control.value && this.isAllClientsSelected(control.value)) {
      control.setValue([this.ALL_CLIENTS_SELECTED, ...options]);
    } else {
      control.setValue([]);
    }
  }

  public toggleClient(
    control: FormControl<MemberModel[]>,
    selectedClients: MemberModel[],
    clickedOption: MemberModel,
  ) {
    if (control.value && this.isAllClientsSelected(control.value)) {
      const newOptions = control.value.filter(
        option =>
          option.memberId !== this.ALL_CLIENTS_SELECTED.memberId &&
          option.memberId !== clickedOption.memberId,
      );
      control.setValue(newOptions);
    }

    if (control.value?.length === selectedClients.length) {
      control.setValue([this.ALL_CLIENTS_SELECTED, ...selectedClients]);
    }
  }

  private filterSelectedClients(selectedClients: MemberModel[]) {
    return selectedClients.filter(
      client => client.memberId !== this.ALL_CLIENTS_SELECTED.memberId,
    );
  }

  public isAllClientsSelected(selected: MemberModel[]) {
    return selected.find(
      option => option.memberId === this.ALL_CLIENTS_SELECTED.memberId,
    );
  }

  public getMemberDisplayName(member: MemberModel) {
    return `${member.memberName} (${member.memberId})`;
  }
}
